// InstanceGameStateCard：把实例 game_state.metrics 解析成富展示卡片。
// 对应旧 SSR instance_detail.html .instance-card 区。
// 字段宽松解析：每一项缺失时降级为 muted "—"。

import { Card, CardContent } from "@/components/ui/Card";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  compactNumber,
  formatCombatPower,
  formatCombatPowerRank,
} from "@/lib/format";

interface MetricsMap {
  id?: number | string;
  name?: string;
}

interface MetricsBar {
  cur?: number;
  max?: number;
}

interface MetricsCurrency {
  id?: number;
  label?: string;
  count?: number;
}

interface MetricsMaterial {
  id?: number;
  label?: string;
  count?: number;
}

interface Metrics {
  map?: MetricsMap;
  hp?: MetricsBar;
  mp?: MetricsBar;
  state?: string;
  currencies?: MetricsCurrency[];
  materials?: MetricsMaterial[];
}

interface RoleVip {
  available?: boolean;
  level?: number | string;
  label?: string;
  channel?: string;
}

interface RoleTitle {
  id?: number | string;
  name?: string;
  active?: boolean;
  time?: number;
}

interface RoleSnapshot {
  combat_power?: number;
  combat_power_rank?: number;
  combat_power_source?: string;
  combat_power_currency_id?: number | string;
  vip?: RoleVip;
  active_title?: RoleTitle;
  titles?: RoleTitle[];
}

export interface InstanceGameStateCardProps {
  instance: Record<string, unknown>;
  accountGameId?: string;
}

function asString(v: unknown): string | undefined {
  if (v === undefined || v === null) return undefined;
  return String(v);
}

function asMetrics(instance: Record<string, unknown>): Metrics | undefined {
  const gs = instance.game_state;
  if (!gs || typeof gs !== "object") return undefined;
  const m = (gs as Record<string, unknown>).metrics;
  if (!m || typeof m !== "object") return undefined;
  return m as Metrics;
}

function asRoleSnapshot(instance: Record<string, unknown>): RoleSnapshot | undefined {
  const gs = instance.game_state;
  if (!gs || typeof gs !== "object") return undefined;
  const role = (gs as Record<string, unknown>).role;
  if (!role || typeof role !== "object") return undefined;
  return role as RoleSnapshot;
}

function meterPct(cur?: number, max?: number): number {
  if (typeof cur !== "number" || typeof max !== "number" || max <= 0) return 0;
  const pct = (cur / max) * 100;
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}

export function InstanceGameStateCard({
  instance,
  accountGameId,
}: InstanceGameStateCardProps) {
  const state = asString(instance.state);
  const peer = asString(instance.peer);
  const roleName = asString(instance.role_name);
  const roleLevel = asString(instance.role_level);
  const gameName = asString(instance.game_name);
  const serverName = asString(instance.server_name);
  const gameId = asString(instance.game_id) ?? accountGameId;
  const metrics = asMetrics(instance);
  const role = asRoleSnapshot(instance);

  return (
    <Card>
      <CardContent className="space-y-3 pt-4 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          {state ? (
            <StatusBadge variant={state === "ready" ? "ok" : "warn"}>
              {state}
            </StatusBadge>
          ) : null}
          {peer ? (
            <span className="font-mono text-[hsl(var(--muted-foreground))]">
              {peer}
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-base font-semibold">
            {roleName ?? "—"}
          </span>
          {roleLevel ? (
            <span className="text-[hsl(var(--muted-foreground))]">
              Lv {roleLevel}
            </span>
          ) : null}
          {gameName ? (
            <>
              <span className="text-[hsl(var(--muted-foreground))]">·</span>
              <span>{gameName}</span>
            </>
          ) : null}
          {serverName ? (
            <>
              <span className="text-[hsl(var(--muted-foreground))]">·</span>
              <span>{serverName}</span>
            </>
          ) : null}
        </div>

        <div className="space-y-2">
          <RoleExtraRows role={role} />
          {!metrics ? (
            <p className="text-[hsl(var(--muted-foreground))]">
              客户端尚未推送 game_state。授权刚通过几秒后会出现。
            </p>
          ) : (
            <>
            <MapRow map={metrics.map} />
            <BarRow label="HP" bar={metrics.hp} fillClass="bg-red-500" />
            <BarRow label="MP" bar={metrics.mp} fillClass="bg-blue-500" />
            {metrics.state ? (
              <div className="flex items-center gap-2">
                <span className="w-12 text-[hsl(var(--muted-foreground))]">
                  角色状态
                </span>
                <StatusBadge variant="neutral">{metrics.state}</StatusBadge>
              </div>
            ) : null}
            <CurrenciesRow
              items={metrics.currencies ?? []}
              gameId={gameId}
            />
            <MaterialsRow items={metrics.materials ?? []} />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RoleExtraRows({ role }: { role?: RoleSnapshot }) {
  if (!role) return null;
  const titleItems = Array.isArray(role.titles) ? role.titles : [];
  const titleVip = vipSummaryFromTitles(titleItems, role.active_title);
  const vipLevel = role.vip?.level ?? titleVip?.level;
  const vipLabel = role.vip?.label || titleVip?.label;
  if (
    role.combat_power === undefined &&
    vipLevel === undefined &&
    !vipLabel &&
    titleItems.length === 0
  ) {
    return null;
  }
  return (
    <>
      {role.combat_power !== undefined ? (
        <div className="flex items-center gap-2">
          <span className="w-12 text-[hsl(var(--muted-foreground))]">
            战斗力
          </span>
          <strong className="font-mono">
            {formatCombatPower(role.combat_power)}
          </strong>
          {formatCombatPowerRank(role.combat_power_rank) ? (
            <span className="text-[hsl(var(--muted-foreground))]">
              战力榜{formatCombatPowerRank(role.combat_power_rank)}
            </span>
          ) : null}
          {role.combat_power_currency_id !== undefined ? (
            <span className="text-[hsl(var(--muted-foreground))]">
              Money #{role.combat_power_currency_id}
            </span>
          ) : null}
        </div>
      ) : null}
      {vipLevel !== undefined || vipLabel ? (
        <div className="flex items-center gap-2">
          <span className="w-12 text-[hsl(var(--muted-foreground))]">
            VIP
          </span>
          <span>
            {vipLabel || (vipLevel !== undefined ? `VIP ${vipLevel}` : "—")}
          </span>
          {vipLabel && vipLevel !== undefined ? (
            <span className="text-[hsl(var(--muted-foreground))]">
              Lv {vipLevel}
            </span>
          ) : null}
        </div>
      ) : null}
      {titleItems.length > 0 ? <TitlesRow items={titleItems} /> : null}
    </>
  );
}

function vipSummaryFromTitles(items: RoleTitle[], activeTitle?: RoleTitle) {
  let best: { level: number; sortLevel: number; label: string } | undefined;
  const activeId = activeTitle?.id === undefined ? undefined : String(activeTitle.id);
  for (const title of items) {
    const parsed = parseVipTitleName(title.name);
    if (!parsed) continue;
    if (title.active || (activeId !== undefined && String(title.id) === activeId)) {
      return parsed;
    }
    if (!best || parsed.sortLevel > best.sortLevel) {
      best = parsed;
    }
  }
  return best;
}

function parseVipTitleName(name?: string) {
  const text = name?.trim() ?? "";
  if (!text) return undefined;
  if (text.includes("顶级VIP")) {
    return { level: 11, sortLevel: 11, label: "顶级VIP" };
  }
  const levelMatch =
    text.match(/([1-9]\d?)\s*级\s*VIP/i) ?? text.match(/VIP\s*([1-9]\d?)/i);
  const level = levelMatch ? Number(levelMatch[1]) : 0;
  if (level >= 1 && level <= 10) {
    return { level, sortLevel: level, label: `${level}级VIP` };
  }
  return undefined;
}

function TitlesRow({ items }: { items: RoleTitle[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-12 text-[hsl(var(--muted-foreground))]">全部称号</span>
      {items.map((title, i) => (
        <span
          key={`${title.id ?? "?"}-${i}`}
          className={
            title.active
              ? "rounded border border-[hsl(var(--primary))] px-2 py-0.5"
              : "rounded border border-[hsl(var(--border))] px-2 py-0.5"
          }
          title={`titleId=${title.id ?? "?"}`}
        >
          {title.name || <code>#{title.id ?? "?"}</code>}
        </span>
      ))}
    </div>
  );
}

function MapRow({ map }: { map?: MetricsMap }) {
  if (!map) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 text-[hsl(var(--muted-foreground))]">地图</span>
      {map.name ? (
        <span>{map.name}</span>
      ) : (
        <span className="text-[hsl(var(--muted-foreground))]">—</span>
      )}
      {map.id !== undefined ? (
        <code className="text-[hsl(var(--muted-foreground))]">#{map.id}</code>
      ) : null}
    </div>
  );
}

function BarRow({
  label,
  bar,
  fillClass,
}: {
  label: string;
  bar?: MetricsBar;
  fillClass: string;
}) {
  if (!bar || typeof bar.cur !== "number" || typeof bar.max !== "number") {
    return null;
  }
  const pct = meterPct(bar.cur, bar.max);
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 text-[hsl(var(--muted-foreground))]">{label}</span>
      <div className="relative h-3 flex-1 overflow-hidden rounded bg-[hsl(var(--muted))]">
        <div
          className={`h-full ${fillClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-32 text-right font-mono">
        {compactNumber(bar.cur)} / {compactNumber(bar.max)}
      </span>
    </div>
  );
}

function CurrenciesRow({
  items,
  gameId,
}: {
  items: MetricsCurrency[];
  gameId?: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-12 text-[hsl(var(--muted-foreground))]">货币</span>
      {items.length === 0 ? (
        <span className="text-[hsl(var(--muted-foreground))]">
          未收到货币余额；
          {gameId ? (
            <a
              href={`/games/${gameId}`}
              className="underline"
            >
              前往游戏配置添加货币
            </a>
          ) : (
            "请前往对应游戏配置添加"
          )}
          ，等待下一个 game_state 上报周期。
        </span>
      ) : (
        items.map((c, i) => (
          <span
            key={`${c.id ?? "?"}-${i}`}
            className="rounded border border-[hsl(var(--border))] px-2 py-0.5"
            title={`currencyId=${c.id ?? "?"}`}
          >
            <span className="text-[hsl(var(--muted-foreground))]">
              {c.label ?? `#${c.id ?? "?"}`}
            </span>{" "}
            <strong>{compactNumber(c.count ?? 0)}</strong>
          </span>
        ))
      )}
    </div>
  );
}

function MaterialsRow({ items }: { items: MetricsMaterial[] }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-12 text-[hsl(var(--muted-foreground))]">材料</span>
      {items.map((m, i) => (
        <span
          key={`${m.id ?? "?"}-${i}`}
          className="rounded border border-[hsl(var(--border))] px-2 py-0.5"
          title={`itemId=${m.id ?? "?"}`}
        >
          {m.label ?? <code>#{m.id ?? "?"}</code>}
          <span className="ml-1 text-[hsl(var(--muted-foreground))]">
            ×{compactNumber(m.count ?? 0)}
          </span>
        </span>
      ))}
    </div>
  );
}
